import { Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import ErrorBoundary from "./calendarErrorBoundary";
import { Scheduler } from "./lib";
import { useCallback, useEffect } from "react";
import { IInterview, IInterviewsListParams } from "../Interviews/InterviewsModel";
import { getDashboardInterviewsList } from "store/slices/employer/interviews/dashboard-interviews-list-slice";
import { convertToFormattedTime } from "./CalendarHelper";


export const Calendar = () => {
    const { interviews } = useAppSelector(state => state.employer.interviews.dashboardInterviewsList);
    const dispatch = useAppDispatch();
    const getInterviews = useCallback(() => {
        const params: IInterviewsListParams = {
            status: ['Scheduled', 'Requested', 'Completed', 'Accepted'],
            pageNo: 1,
            pageSize: 200,
            sort:  'asc',
            sortBy: 'alphabetical'
        };
        dispatch(getDashboardInterviewsList(params));
    }, [dispatch]);
    

    useEffect(() => {
        if (interviews?.length === 0) {
            getInterviews();
        }
    }, [getInterviews, interviews?.length,]);

    const createCalendarEvents = (interviews: IInterview[]): any[] => {
        const currentDate = new Date();
    
        const events = interviews.flatMap((interview) => {
            const interviewEvents = [];
    
            // 1. Add confirmed interview event if within range
            const scheduleDate = new Date(interview.schedule_date);
            const isWithinRange =
                scheduleDate >= new Date(currentDate.getTime() - 730 * 24 * 60 * 60 * 1000) &&
                scheduleDate <= new Date(currentDate.getTime() + 730 * 24 * 60 * 60 * 1000);
    
            if (isWithinRange) {
                const scheduleDateStr = interview.schedule_date.replace(/-/g, '/');
                const fromTime = convertToFormattedTime(interview.from_time);
                const toTime = convertToFormattedTime(interview.to_time);

                interviewEvents.push({
                    event_id: `confirmed-${interview.id}`,
                    title: `${interview.status} Interview: ${interview.candidate.account.first_name} ${interview.candidate.account.last_name}`,
                    start: new Date(`${scheduleDateStr} ${fromTime}`),
                    end: new Date(`${scheduleDateStr} ${toTime}`),
                    color: "#50b500", // green
                    draggable: false,
                    editable: false,
                    deletable: false,
                });
            }
            const isAccepted = interview.status === 'Accepted';
            const timeSlots = interview?.request_time_slots ?? interview?.candidate_requested_time_slots ?? [];
            const acceptedIndices = interview?.accepted_request_time_slots ?? [];

            timeSlots.forEach((slot: any, index: number) => {
                if (!slot?.date || !slot?.from_time || !slot?.to_time) return;

                // If interview is Accepted, skip non-accepted indices
                if (isAccepted && !acceptedIndices.includes(index)) return;

                const dateStr = slot.date.replace(/-/g, '/');
                const start = new Date(`${dateStr} ${slot.from_time}`);
                const end = new Date(`${dateStr} ${slot.to_time}`);

                interviewEvents.push({
                    event_id: `requested-${interview.id}-${index}`,
                    title: `${isAccepted ? 'Accepted Slot' : 'Requested Slot'}: ${interview.candidate.account.first_name} ${interview.candidate.account.last_name}`,
                    start,
                    end,
                    color: isAccepted ? "#50b500" : "#FFA500", // Green if accepted, orange if just a request
                    draggable: false,
                    editable: false,
                    deletable: false,
                });
            });
            return interviewEvents;
        });
    
        return events;
    };
    const calendarEvents = createCalendarEvents(interviews);

    return (
        <ErrorBoundary>
            <Stack>
                {/* Render the Scheduler component wrapped with ErrorBoundary */}
                <Scheduler
                    editable={false}
                    events={calendarEvents}
                    week={{ weekDays: [0, 1, 2, 3, 4, 5, 6], weekStartOn: 6, startHour: 0, endHour: 24, step: 30 }}
                    day={{ startHour: 0, endHour: 24, step: 30 }}
                />
            </Stack>
        </ErrorBoundary>
    );
}